import {
  ApiBlueprint,
  createFrontendFeatureLoader,
  createFrontendPlugin,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import {
  EntityCardBlueprint,
  EntityContentBlueprint,
} from '@backstage/plugin-catalog-react/alpha';
import SchoolIcon from '@material-ui/icons/School';
import { onboardingApiRef, OnboardingApiClient } from '../api';
import { rootRouteRef } from '../routes';
import {
  LEARNING_CONTENT_KIND,
  ONBOARDING_COURSE_TYPE,
  ONBOARDING_THEME_TYPE,
  ONBOARDING_UNIT_TYPE,
} from '../lib/relations';

export { NfsLearnPage as LearnPage } from '../components/LearnPage';

const themeFilter = {
  kind: LEARNING_CONTENT_KIND,
  'spec.type': ONBOARDING_THEME_TYPE,
};

const courseFilter = {
  kind: LEARNING_CONTENT_KIND,
  'spec.type': ONBOARDING_COURSE_TYPE,
};

const unitFilter = {
  kind: LEARNING_CONTENT_KIND,
  'spec.type': ONBOARDING_UNIT_TYPE,
};

/**
 * Entity overview card listing the onboarding courses that belong to a
 * LearningContent theme.
 */
const themeOverviewCard = EntityCardBlueprint.make({
  name: 'theme-progress',
  params: {
    filter: themeFilter,
    loader: () =>
      import('../components/OnboardingThemeContent').then(m => (
        <m.OnboardingThemeCourseList />
      )),
  },
});

/**
 * Entity page tab that displays the member-centered onboarding matrix for a
 * LearningContent theme, broken down by participant group (team).
 */
const themeProgressContent = EntityContentBlueprint.make({
  name: 'theme-team-progress',
  params: {
    path: '/team-progress',
    title: 'チーム進捗',
    filter: themeFilter,
    loader: () =>
      import('../components/OnboardingThemeContent').then(m => (
        <m.OnboardingThemeContent />
      )),
  },
});

/**
 * Entity overview card that displays per-member course progress for a
 * LearningContent course.
 */
const courseOverviewCard = EntityCardBlueprint.make({
  name: 'course-progress',
  params: {
    filter: courseFilter,
    loader: () =>
      import('../components/OnboardingCourseContent').then(m => (
        <m.OnboardingCourseUnitList />
      )),
  },
});

/**
 * Entity page tab that displays the per-member progress of a LearningContent
 * course, broken down by participant group (team).
 */
const courseProgressContent = EntityContentBlueprint.make({
  name: 'course-team-progress',
  params: {
    path: '/team-progress',
    title: 'チーム進捗',
    filter: courseFilter,
    loader: () =>
      import('../components/OnboardingCourseContent').then(m => (
        <m.OnboardingCourseContent />
      )),
  },
});

/**
 * Entity overview card that lets the signed-in user mark a LearningContent
 * unit (Tip) as completed.
 */
const unitOverviewCard = EntityCardBlueprint.make({
  name: 'unit-completion',
  params: {
    filter: unitFilter,
    loader: () =>
      import('../components/OnboardingUnitContent').then(m => (
        <m.OnboardingUnitContent />
      )),
  },
});

/**
 * Registers the {@link onboardingApiRef} API, backed by
 * `@platt/plugin-platt-onboarding-backend`.
 */
const onboardingApi = ApiBlueprint.make({
  params: defineParams =>
    defineParams({
      api: onboardingApiRef,
      deps: {
        discoveryApi: discoveryApiRef,
        fetchApi: fetchApiRef,
      },
      factory: ({ discoveryApi, fetchApi }) =>
        new OnboardingApiClient({ discoveryApi, fetchApi }),
    }),
});

/**
 * Top-level "Learn" page listing every onboarding theme as a card. Appears
 * in the sidebar nav automatically because it declares a `title`/`icon` and
 * a `routeRef`.
 */
const learnPage = PageBlueprint.make({
  params: {
    path: '/learn',
    title: 'Learn',
    icon: <SchoolIcon />,
    routeRef: rootRouteRef,
    loader: () =>
      import('../components/LearnPage').then(m => <m.NfsLearnPage />),
  },
});

const internalPlugin = createFrontendPlugin({
  pluginId: 'platt-onboarding',
  info: {
    packageJson: () => import('../../package.json'),
  },
  routes: {
    learn: rootRouteRef,
  },
  extensions: [
    themeOverviewCard,
    themeProgressContent,
    courseOverviewCard,
    courseProgressContent,
    unitOverviewCard,
    onboardingApi,
    learnPage,
  ],
});

export default createFrontendFeatureLoader({
  async *loader({ config }) {
    if (config.getOptionalBoolean('platt.onboarding.enabled')) {
      yield internalPlugin;
    }
  },
});
